// Aba "WhatsApp" da negociação: as conversas do Inbox com o número do cliente.
//
// A conversa entra pelo número cadastrado na negociação (ver
// `supabase/functions/_shared/conversasNegociacao.ts`), e quem decide quem vê é
// o servidor: adm, gerente e o responsável pela negociação.
//
// Desde 23/10/2026 dá para RESPONDER por aqui, sem ir ao Inbox: a mensagem sai
// pelo WhatsApp da conexão (o da Júlia, hoje) com o nome de quem enviou. A aba
// se atualiza sozinha a cada 20 s para mostrar a resposta do cliente e o
// status de entrega.
//
// WhatsApp completo (23/10/2026, mesma tarde): anexos, mensagem de voz,
// figurinhas, emoji, responder citando e reagir — o mesmo do Inbox
// (`Inbox/envioRico.ts` + `Composer`), trocando só o caminho até o servidor.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Lock, MessageSquare, Phone, RefreshCw, ShieldAlert, Sparkles, Users, WifiOff } from 'lucide-react';
import Balao from '../../../Omnichannel/Inbox/componentes/Balao';
import Composer from '../../../Omnichannel/Inbox/componentes/Composer';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { carimboLista, chaveDoDia, formatarTelefone, nomeExibicao, separadorDeDia } from '../../../Omnichannel/Inbox/formato';
import type { ConexaoPublica, Mensagem } from '../../../Omnichannel/Inbox/tipos';
import {
  carregarConversasDaNegociacao,
  type ConversaDaNegociacao, type RespostaConversas,
} from './apiConversasWhatsApp';
import {
  criarAcoes, indicePorProvider, resumoCitacao, transporteNegociacao, useEnvioRico,
} from '../../../Omnichannel/Inbox/envioRico';

interface Props {
  negociacaoId: number;
}

type Liberada = Extract<RespostaConversas, { permitido: true }>;

const ORIGEM: Record<string, string | null> = {
  whatsapp: null,
  whatsapp_financeiro: 'WhatsApp do financeiro',
  vinculo_manual: 'Vinculada no Inbox',
};

/** Menos de 5 min e mesma direção: encosta no balão anterior (igual ao Inbox). */
function ehAgrupada(atual: Mensagem, anterior: Mensagem | undefined): boolean {
  if (!anterior) return false;
  if (anterior.direction !== atual.direction || anterior.sender_user_id !== atual.sender_user_id) return false;
  const dt = new Date(atual.provider_timestamp).getTime() - new Date(anterior.provider_timestamp).getTime();
  return dt >= 0 && dt < 5 * 60 * 1000;
}

function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const PERTO_DO_FIM = 120; // px

/** De quanto em quanto tempo a aba busca mensagem nova sozinha. */
const ATUALIZAR_A_CADA_MS = 20_000;

/**
 * Junta a página mais recente com o que já está na tela.
 *
 * Mensagem que já estava fica com os anexos de antes: a URL assinada muda a
 * cada busca, e trocar faria a imagem recarregar (piscar) a cada 20 s.
 * Mensagem local (envio que falhou) continua na tela até alguém recarregar.
 */
function mesclar(atuais: Mensagem[], novas: Mensagem[]): Mensagem[] {
  const porId = new Map(atuais.map((m) => [m.id, m]));
  for (const n of novas) {
    const antes = porId.get(n.id);
    porId.set(n.id, antes && (antes.anexos?.length ?? 0) === (n.anexos?.length ?? 0) ? { ...n, anexos: antes.anexos } : n);
  }
  return [...porId.values()].sort((a, b) => a.provider_timestamp.localeCompare(b.provider_timestamp));
}

const ETAPA: Record<string, string> = {
  primeiro_contato: 'primeiro contato',
  qualificacao: 'qualificação',
  apresentacao: 'apresentação',
  simulacao_proposta: 'simulação / proposta',
  objecoes_negociacao: 'objeções / negociação',
  documentos_assinatura: 'documentos / assinatura',
  fechado: 'fechado',
  perdido: 'perdido',
  pos_venda_suporte: 'pós-venda / suporte',
};

function corChance(p: number | null): string {
  if (p == null) return 'bg-gray-300';
  if (p >= 60) return 'bg-emerald-500';
  if (p >= 30) return 'bg-amber-500';
  return 'bg-rose-400';
}

const ConversasWhatsApp: React.FC<Props> = ({ negociacaoId }) => {
  const { user, profile } = useAuth();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [permitido, setPermitido] = useState<boolean | null>(null);
  const [dados, setDados] = useState<Omit<Liberada, 'mensagens' | 'temMaisAntigas' | 'conversaId' | 'permitido'> | null>(null);
  const [conversaId, setConversaId] = useState<string | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [temMaisAntigas, setTemMaisAntigas] = useState(false);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);

  const areaRef = useRef<HTMLDivElement>(null);
  // Distância até o fim antes de entrar a página antiga: é o que segura o
  // ponto de leitura quando o conteúdo cresce por cima.
  const distanciaDoFimRef = useRef<number | null>(null);
  // Enquanto a pessoa está no fim, imagem que termina de carregar não pode
  // deixar a última mensagem fora da tela.
  const grudadoNoFimRef = useRef(true);
  // Resposta de um pedido antigo (troca rápida de conversa) não sobrescreve a nova.
  const pedidoRef = useRef(0);

  const carregar = useCallback(async (alvo: string | null) => {
    const pedido = ++pedidoRef.current;
    setCarregando(true);
    setErro(null);
    try {
      const r = await carregarConversasDaNegociacao({ negociacaoId, conversationId: alvo });
      if (pedido !== pedidoRef.current) return;
      setPermitido(r.permitido);
      if (!r.permitido) return;
      const { mensagens: msgs, temMaisAntigas: mais, conversaId: id, permitido: _p, ...resto } = r;
      grudadoNoFimRef.current = true;
      distanciaDoFimRef.current = null;
      setDados(resto);
      setConversaId(id);
      setMensagens(msgs);
      setTemMaisAntigas(mais);
    } catch (e) {
      if (pedido !== pedidoRef.current) return;
      setErro((e as Error).message);
    } finally {
      if (pedido === pedidoRef.current) setCarregando(false);
    }
  }, [negociacaoId]);

  useEffect(() => {
    carregar(null);
  }, [carregar]);

  const carregarAntigas = useCallback(async () => {
    if (!conversaId || !mensagens.length || carregandoAntigas) return;
    const el = areaRef.current;
    setCarregandoAntigas(true);
    try {
      const r = await carregarConversasDaNegociacao({
        negociacaoId,
        conversationId: conversaId,
        anteriorA: mensagens[0].provider_timestamp,
      });
      if (!r.permitido) return;
      if (el) distanciaDoFimRef.current = el.scrollHeight - el.scrollTop;
      grudadoNoFimRef.current = false;
      setMensagens((atuais) => [...r.mensagens.filter((m) => !atuais.some((a) => a.id === m.id)), ...atuais]);
      setTemMaisAntigas(r.temMaisAntigas);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregandoAntigas(false);
    }
  }, [conversaId, mensagens, carregandoAntigas, negociacaoId]);

  // ── Atualização sozinha: resposta do cliente e status de entrega ──
  // Busca a página mais recente da conversa aberta e junta com a tela, sem
  // spinner e sem mexer na rolagem de quem está lendo mensagem antiga.
  const atualizandoRef = useRef(false);
  const atualizarRecentes = useCallback(async () => {
    if (!conversaId || atualizandoRef.current) return;
    atualizandoRef.current = true;
    const pedido = pedidoRef.current;
    try {
      const r = await carregarConversasDaNegociacao({ negociacaoId, conversationId: conversaId });
      // Trocou de conversa no meio: esta resposta é de outra.
      if (pedido !== pedidoRef.current || !r.permitido || r.conversaId !== conversaId) return;
      const { mensagens: msgs, temMaisAntigas: _m, conversaId: _c, permitido: _p, ...resto } = r;
      setDados(resto);
      setMensagens((atuais) => mesclar(atuais, msgs));
    } catch {
      // Falha na atualização automática não vira erro na tela: a próxima tenta de novo.
    } finally {
      atualizandoRef.current = false;
    }
  }, [conversaId, negociacaoId]);

  useEffect(() => {
    if (!conversaId) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') atualizarRecentes();
    }, ATUALIZAR_A_CADA_MS);
    return () => window.clearInterval(id);
  }, [conversaId, atualizarRecentes]);

  // ── Responder (texto, arquivo, voz, figurinha, citação, reação) ──
  // A conexão desta conversa decide se dá para mandar mídia: hoje só a
  // Evolution (WhatsApp da Júlia) está ligada e testada.
  const conversaSelecionada = dados?.conversas.find((c) => c.id === conversaId);
  const ehWhatsApp = (conversaSelecionada?.canal ?? 'whatsapp_evolution') === 'whatsapp_evolution';
  const acoes = useMemo(
    () => (conversaId && ehWhatsApp ? criarAcoes(transporteNegociacao(negociacaoId, conversaId)) : null),
    [conversaId, ehWhatsApp, negociacaoId],
  );
  const [respondendo, setRespondendo] = useState<Mensagem | null>(null);
  useEffect(() => { setRespondendo(null); }, [conversaId]);
  const rico = useEnvioRico({
    acoes,
    conversaId,
    userId: user?.id ?? null,
    aplicar: setMensagens,
    // O worker entrega em segundos: duas buscas curtas mostram o "enviado".
    aoEnviar: () => {
      window.setTimeout(atualizarRecentes, 4000);
      window.setTimeout(atualizarRecentes, 12000);
    },
  });
  // Quem acabou de mandar quer ver a própria mensagem: volta para o fim.
  const comCitacao = <T,>(fazer: (citada: Mensagem | null) => T) => {
    grudadoNoFimRef.current = true;
    const citada = respondendo;
    setRespondendo(null);
    return fazer(citada);
  };

  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    if (distanciaDoFimRef.current != null) {
      el.scrollTop = el.scrollHeight - distanciaDoFimRef.current;
      distanciaDoFimRef.current = null;
    } else if (grudadoNoFimRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [mensagens]);

  // Só a PESSOA tira a conversa do fim. O evento de rolagem chega depois que
  // áudio e imagem já mudaram a altura, e o próprio navegador rola sozinho para
  // compensar — julgar só pela posição fazia a tela "desgrudar" do fim sem
  // ninguém ter tocado nela (medido na 7809: parava 965 px acima do fim).
  const ultimoGestoRef = useRef(0);
  const marcarGesto = () => { ultimoGestoRef.current = Date.now(); };

  const aoRolar = () => {
    const el = areaRef.current;
    if (!el) return;
    const perto = el.scrollHeight - el.scrollTop - el.clientHeight < PERTO_DO_FIM;
    if (perto) grudadoNoFimRef.current = true;
    else if (Date.now() - ultimoGestoRef.current < 1500) grudadoNoFimRef.current = false;
  };

  // Áudio, imagem e documento mudam de tamanho DEPOIS que a conversa já rolou
  // para o fim (a URL chega, a imagem carrega). Sem isto a última mensagem
  // ficava ~400 px abaixo da área visível. Observa o conteúdo e, se a pessoa
  // está no fim, mantém no fim. O callback roda antes do evento de rolagem que
  // o ajuste automático do navegador dispara, então não perde o "grudado".
  const observadorRef = useRef<ResizeObserver | null>(null);
  const conteudoRef = useCallback((no: HTMLDivElement | null) => {
    observadorRef.current?.disconnect();
    observadorRef.current = null;
    if (!no || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(() => {
      const el = areaRef.current;
      if (el && grudadoNoFimRef.current) el.scrollTop = el.scrollHeight;
    });
    obs.observe(no);
    observadorRef.current = obs;
  }, []);

  // ── Estados sem conversa ──

  if (carregando && permitido === null) {
    return (
      <div className="flex min-h-[140px] items-center justify-center">
        <Loader2 size={20} className="animate-spin text-gray-300 dark:text-slate-600" />
      </div>
    );
  }

  if (erro && !dados) {
    return (
      <div className="flex min-h-[140px] flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>
        <button
          onClick={() => carregar(conversaId)}
          className="text-xs font-semibold text-primary hover:underline"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  if (permitido === false) {
    return (
      <div className="flex min-h-[140px] flex-col items-center justify-center gap-2 px-6 text-center text-gray-500 dark:text-slate-400">
        <Lock size={22} className="text-gray-300 dark:text-slate-600" />
        <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Histórico restrito</p>
        <p className="max-w-sm text-xs">
          As conversas de WhatsApp ficam visíveis para administradores, gerentes e o responsável por esta negociação.
        </p>
      </div>
    );
  }

  if (!dados) return null;

  const conversa: ConversaDaNegociacao | undefined = dados.conversas.find((c) => c.id === conversaId);

  const avisoForaDoAr = dados.conexoesForaDoAr.length > 0 && (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200">
      <WifiOff size={14} className="mt-0.5 shrink-0" />
      <div className="space-y-0.5">
        {dados.conexoesForaDoAr.map((c) => (
          <p key={c.nome}>
            <strong>{c.nome}</strong> está desconectado do VigorHub desde {dataCurta(c.ultima_mensagem_em)}.
            O que foi conversado depois não aparece aqui.
          </p>
        ))}
      </div>
    </div>
  );

  if (dados.numeros.length === 0) {
    return (
      <div className="space-y-3">
        {avisoForaDoAr}
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 text-center text-gray-500 dark:text-slate-400">
          <Phone size={22} className="text-gray-300 dark:text-slate-600" />
          <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Sem WhatsApp cadastrado</p>
          <p className="max-w-sm text-xs">Cadastre o WhatsApp do cliente nos dados da negociação para ver as conversas aqui.</p>
        </div>
      </div>
    );
  }

  if (dados.conversas.length === 0) {
    return (
      <div className="space-y-3">
        {avisoForaDoAr}
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 text-center text-gray-500 dark:text-slate-400">
          <MessageSquare size={22} className="text-gray-300 dark:text-slate-600" />
          <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Nenhuma conversa encontrada</p>
          <p className="max-w-sm text-xs">
            Nenhum WhatsApp conectado ao VigorHub conversou com{' '}
            {dados.numeros.map((n) => formatarTelefone(n.telefone)).join(' ou ')}.
          </p>
          <button
            onClick={() => carregar(null)}
            disabled={carregando}
            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline disabled:opacity-50"
          >
            <RefreshCw size={12} className={carregando ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>
    );
  }

  // ── Conversa ──

  let diaAnterior = '';
  const origem = conversa ? ORIGEM[conversa.origem] : null;
  const porProvider = indicePorProvider(mensagens);
  // Para a citação mostrar "você" com o nome certo mesmo antes de a página trazer os nomes.
  const nomesComigo = user?.id && profile?.full_name ? { [user.id]: profile.full_name, ...dados.nomes } : dados.nomes;
  // Aviso de sistema sem texto não é conversa — e sozinho num dia deixaria só a data solta.
  const visiveis = mensagens.filter((m) => !(m.type === 'system' && !m.body?.trim()));

  return (
    <div className="space-y-3">
      {avisoForaDoAr}

      {dados.conversas.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {dados.conversas.map((c) => (
            <button
              key={c.id}
              onClick={() => c.id !== conversaId && carregar(c.id)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                c.id === conversaId
                  ? 'border-primary/30 bg-primary/10 text-primary dark:bg-primary/20'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
              }`}
            >
              {c.conexao_nome}
              <span className="ml-1.5 text-gray-400 dark:text-slate-500">{carimboLista(c.ultima_mensagem_em)}</span>
            </button>
          ))}
        </div>
      )}

      {conversa && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-slate-400">
          <span className="font-semibold text-gray-800 dark:text-slate-100">
            {nomeExibicao(conversa.contato_nome, conversa.contato_telefone)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Phone size={11} />
            {formatarTelefone(conversa.contato_telefone)}
          </span>
          <span className="text-gray-300 dark:text-slate-600">·</span>
          <span>{conversa.conexao_nome}</span>
          {origem && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-slate-800 dark:text-slate-300">
              {origem}
            </span>
          )}
          <button
            onClick={() => carregar(conversaId)}
            disabled={carregando}
            title="Buscar mensagens novas"
            className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary disabled:opacity-50 dark:hover:bg-slate-800"
          >
            <RefreshCw size={12} className={carregando ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      )}

      {conversa && dados.analises?.[conversa.id] && (() => {
        const a = dados.analises[conversa.id];
        return (
          // Análise da IA (#6313): chance de fechamento e próximo passo, do último dia analisado.
          <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs dark:border-primary/30 dark:bg-primary/10">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1 font-semibold text-primary dark:text-[#B4C77A]">
                <Sparkles size={12} /> Análise da IA · {dataCurta(`${a.dia}T12:00:00Z`)}
              </span>
              {a.comercial && a.probabilidade != null ? (
                <span className="inline-flex items-center gap-1.5 text-gray-700 dark:text-slate-200">
                  Chance de fechar
                  <span className="h-1.5 w-14 overflow-hidden rounded-full bg-white/80 dark:bg-slate-800">
                    <span className={`block h-full ${corChance(a.probabilidade)}`} style={{ width: `${a.probabilidade}%` }} />
                  </span>
                  <strong>{a.probabilidade}%</strong>
                  {a.temperatura && <span className="text-gray-500 dark:text-slate-400">· {a.temperatura}</span>}
                  {a.etapa && <span className="text-gray-500 dark:text-slate-400">· {ETAPA[a.etapa] ?? a.etapa}</span>}
                </span>
              ) : (
                <span className="text-gray-500 dark:text-slate-400">conversa que não é de venda</span>
              )}
              {a.alertas > 0 && (
                <span className="inline-flex items-center gap-1 font-semibold text-rose-700 dark:text-rose-300">
                  <ShieldAlert size={12} /> {a.alertas} promessa{a.alertas > 1 ? 's' : ''} fora da política
                </span>
              )}
            </div>
            {a.proximo_passo && (
              <p className="mt-1 text-gray-700 dark:text-slate-200"><span className="font-semibold">Próximo passo:</span> {a.proximo_passo}</p>
            )}
          </div>
        );
      })()}

      {conversa && conversa.outras_negociacoes > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200">
          <Users size={14} className="mt-0.5 shrink-0" />
          <p>
            Este número também está em {conversa.outras_negociacoes === 1 ? 'outra negociação' : `outras ${conversa.outras_negociacoes} negociações`}.
            Pode ser o telefone de um parceiro ou parente, e a conversa pode ser sobre outro cliente.
          </p>
        </div>
      )}

      {erro && (
        <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
          <AlertTriangle size={13} />
          {erro}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700">
      <div
        ref={areaRef}
        onScroll={aoRolar}
        onWheel={marcarGesto}
        onTouchMove={marcarGesto}
        onPointerDown={marcarGesto}
        onKeyDown={marcarGesto}
        data-area-conversa
        // Altura MÁXIMA, não fixa: conversa curta ocupa pouco, conversa longa
        // para no teto e rola por dentro. O teto acompanha a altura da tela (o
        // modal ocupa 90% dela) e já desconta a caixa de escrever.
        // A barra de rolagem fica visível sempre que há o que rolar.
        style={{
          maxHeight: 'clamp(240px, calc(90vh - 500px), 520px)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(100, 116, 139, 0.55) transparent',
          scrollbarGutter: 'stable',
        }}
        className="relative overflow-y-auto bg-gray-50/70 px-3 py-2 dark:bg-slate-950/40"
      >
        {carregando && (
          <div className="pointer-events-none absolute inset-x-0 top-2 z-10 flex justify-center">
            <Loader2 size={16} className="animate-spin text-gray-400" />
          </div>
        )}

        {visiveis.length === 0 && !carregando && (
          <div className="flex items-center justify-center py-10">
            <p className="text-xs text-gray-400 dark:text-slate-500">Nenhuma mensagem nesta conversa.</p>
          </div>
        )}

        <div ref={conteudoRef}>
        {temMaisAntigas && (
          <div className="flex justify-center pb-2">
            <button
              onClick={carregarAntigas}
              disabled={carregandoAntigas}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-gray-500 shadow-sm ring-1 ring-gray-200 transition-colors hover:text-primary disabled:opacity-60 dark:bg-slate-800/80 dark:text-slate-400 dark:ring-slate-600"
            >
              {carregandoAntigas && <Loader2 size={12} className="animate-spin" />}
              {carregandoAntigas ? 'Carregando…' : 'Ver mensagens anteriores'}
            </button>
          </div>
        )}

        {visiveis.map((m, i) => {
          const dia = chaveDoDia(m.provider_timestamp);
          const mostraDia = dia !== diaAnterior;
          diaAnterior = dia;
          return (
            <React.Fragment key={m.id}>
              {mostraDia && (
                // Sem `sticky`: numa área tão baixa, as datas grudadas no topo
                // ficam umas por cima das outras ao rolar.
                <div className="flex justify-center py-2">
                  <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm ring-1 ring-gray-200/80 backdrop-blur dark:bg-slate-900/90 dark:text-slate-400 dark:ring-slate-700">
                    {separadorDeDia(m.provider_timestamp)}
                  </span>
                </div>
              )}
              <Balao
                mensagem={m}
                autor={m.sender_user_id
                  ? dados.nomes[m.sender_user_id] ?? (m.sender_user_id === user?.id ? profile?.full_name ?? null : null)
                  : null}
                agrupado={!mostraDia && ehAgrupada(m, visiveis[i - 1])}
                citada={m.reply_to_external_id
                  ? resumoCitacao(porProvider.get(m.reply_to_external_id), nomesComigo, conversa?.contato_nome)
                  : null}
                onResponder={acoes ? setRespondendo : undefined}
                onReagir={acoes ? rico.reagir : undefined}
              />
            </React.Fragment>
          );
        })}
        </div>
      </div>

      {conversa && (
        <Composer
          conexao={conexaoDoComposer(conversa)}
          enviando={rico.enviando}
          erro={rico.erro}
          onEnviar={(texto) => comCitacao((citada) => rico.enviarTexto(texto, citada))}
          onLimparErro={rico.limparErro}
          onEnviarArquivos={acoes ? (itens, legenda) => comCitacao((citada) => rico.enviarArquivos(itens, legenda, citada)) : undefined}
          onEnviarFigurinha={acoes ? (f) => comCitacao((citada) => rico.enviarFigurinha(f, citada)) : undefined}
          listarFigurinhas={acoes ? acoes.listarFigurinhas : undefined}
          respondendo={respondendo ? resumoCitacao(respondendo, nomesComigo, conversa.contato_nome) : null}
          onCancelarResposta={() => setRespondendo(null)}
        />
      )}
      </div>

      <p className="text-[11px] text-gray-400 dark:text-slate-500">
        {conversa
          ? `As mensagens saem pelo ${conversa.conexao_nome}, com o seu nome registrado. A conversa se atualiza sozinha a cada 20 segundos.`
          : 'Mensagens trocadas pelo WhatsApp conectado ao Inbox do VigorHub.'}
      </p>
    </div>
  );
};

/**
 * O compositor do Inbox decide pela conexão (status e capabilities). Canal que
 * não está "ativo" vai como desconectado: o servidor recusa o envio de
 * qualquer jeito, e assim o aviso aparece antes de a pessoa escrever.
 */
function conexaoDoComposer(c: ConversaDaNegociacao): ConexaoPublica {
  return {
    id: '',
    org_id: '',
    channel_type: (c.canal ?? 'whatsapp_evolution') as ConexaoPublica['channel_type'],
    display_name: c.conexao_nome,
    external_id: '',
    status: c.conexao_status === 'active' ? 'active' : 'disconnected',
    capabilities: (c.conexao_capabilities ?? {}) as ConexaoPublica['capabilities'],
    metadata: {},
    last_error: null,
    last_seen_at: null,
  };
}

export default ConversasWhatsApp;
